import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Lock, Eye, EyeOff, Building2, Loader2, Download, Settings, Play, MapPin, ExternalLink, CheckCircle, AlertTriangle, Filter, Send, Bot, Search, Globe } from 'lucide-react';
import jsPDF from 'jspdf';
const DEAL_PASSWORD = 'STCapital123';
const REALISTIC_DEAL_SOURCING_PROMPT = `You are a Deal Sourcing Assistant helping users find business opportunities in Massachusetts.

IMPORTANT REALITY CHECK:
- You CANNOT browse websites or access real-time listing data
- You CANNOT scrape BizBuySell, BizQuest, or LoopNet
- You CAN provide intelligent guidance on WHERE and HOW to search

YOUR JOB:
1. Generate SPECIFIC search URLs with exact filters pre-applied
2. Provide search strategy and tips for each platform
3. Explain what to look for in listings
4. Help analyze deals the user finds and pastes to you
5. Calculate valuations and financing scenarios when given deal data

RESPONSE FORMAT:

When user asks to "find deals" or "scan", respond with:

{
  "search_strategy": {
    "platforms": [
      {
        "name": "BizBuySell",
        "search_url": "https://www.bizbuysell.com/businesses-for-sale/massachusetts/?q=&s=newest&price_min=500000&price_max=5000000",
        "filters_to_apply": [
          "Price: $500K - $5M",
          "Location: Massachusetts",
          "Sort by: Newest First",
          "Cash Flow: $150K+"
        ],
        "what_to_look_for": [
          "Check 'Date Listed' - must be within 30 days",
          "Look for 'Seller Financing Available' badge",
          "Verify SDE/Cash Flow is disclosed",
          "Check if real estate is included"
        ]
      },
      {
        "name": "BizQuest",
        "search_url": "https://www.bizquest.com/businesses-for-sale/massachusetts/?price_low=500000&price_high=5000000&sort=date",
        "filters_to_apply": [
          "Price Range: $500K - $5M",
          "State: Massachusetts",
          "Sort: Most Recent"
        ],
        "what_to_look_for": [
          "Review 'Asking Price' and 'Cash Flow'",
          "Note broker contact information",
          "Check listing date (must be recent)"
        ]
      },
      {
        "name": "LoopNet",
        "search_url": "https://www.loopnet.com/search/commercial-real-estate/massachusetts/for-sale/?sk=9d8f0c8c8e8&bb=2qwm0j0vN1kv_jvyN",
        "filters_to_apply": [
          "Property Type: Business Opportunity",
          "Location: Massachusetts",
          "Price: $500K - $5M"
        ],
        "what_to_look_for": [
          "Focus on 'Business Opportunity' category",
          "Check if business + real estate or business only",
          "Review financial details in listing"
        ]
      }
    ],
    "next_steps": [
      "1. Click each search URL above - they have your filters pre-applied",
      "2. Browse the listings and find 3-5 that match your criteria",
      "3. Copy the listing details (title, price, cash flow, location, broker info, listing URL)",
      "4. Paste them back here and I'll analyze them for you",
      "5. I'll calculate valuations, financing scenarios, and provide recommendations"
    ],
    "pro_tips": [
      "Look for listings with 'Verified' or 'Featured' badges - higher quality",
      "Prioritize listings with disclosed financials (revenue, SDE, cash flow)",
      "Check broker reputation - established firms = better deals",
      "Save listings to compare - don't decide on first one you see"
    ]
  }
}

When user PASTES deal information, analyze it and provide:
- Valuation analysis (Price/SDE multiple)
- 3 financing scenarios (all-cash, seller financing, SBA)
- Risk assessment
- Recommendation (Pursue/Watch/Pass)

Be helpful, honest, and practical. Guide them to find real deals themselves.`;
interface DealParameters {
  minPrice: number;
  maxPrice: number;
  minSDE: number;
  maxSDE: number;
  minCashFlow: number;
  maxRent: number;
  minRevenue: number;
  state: string;
  geography: string;
  businessAge: number;
  maxBusinessAge: number;
  userAddress: string;
  minEmployees: number;
  maxEmployees: number;
  profitMargin: number;
}
export const DealSourcingAgent: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [showSettings, setShowSettings] = useState(true);
  const [messages, setMessages] = useState<any[]>([]);
  const [input, setInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [params, setParams] = useState<DealParameters>({
    minPrice: 500000,
    maxPrice: 5000000,
    minSDE: 150000,
    maxSDE: 1000000,
    minCashFlow: 100000,
    maxRent: 10000,
    minRevenue: 500000,
    state: 'Massachusetts',
    geography: 'Greater Boston',
    businessAge: 5,
    maxBusinessAge: 50,
    userAddress: '',
    minEmployees: 1,
    maxEmployees: 50,
    profitMargin: 15
  });
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'D') {
        e.preventDefault();
        setIsOpen(true);
      }
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  const handleAuth = () => {
    if (password === DEAL_PASSWORD) {
      setIsAuthenticated(true);
      setMessages([{
        role: 'system',
        content: 'Deal Sourcing Assistant ready. I\'ll help you find and analyze business opportunities. Click "Generate Search Links" to get started, or paste deal details for analysis.'
      }]);
    } else {
      alert('Incorrect password');
      setPassword('');
    }
  };
  const handleSendMessage = async (messageContent?: string) => {
    const content = messageContent || input;
    if (!content.trim() || isScanning) return;
    setInput('');
    setIsScanning(true);
    const userMessage = {
      role: 'user',
      content: content
    };
    setMessages(prev => [...prev, userMessage]);
    try {
      const response = await fetch('https://requests.magicpatterns.com/openai/proxy', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          model: 'gpt-4o',
          messages: [{
            role: 'system',
            content: REALISTIC_DEAL_SOURCING_PROMPT
          }, ...messages.filter((m: any) => m.role !== 'system'), userMessage],
          temperature: 0.7,
          max_tokens: 3000
        })
      });
      const data = await response.json();
      let assistantMessage = data.choices?.[0]?.message?.content;
      if (assistantMessage) {
        const jsonMatch = assistantMessage.match(/```json\n([\s\S]*?)\n```/) || assistantMessage.match(/```\n([\s\S]*?)\n```/);
        if (jsonMatch) {
          assistantMessage = jsonMatch[1];
        }
        let searchData = null;
        try {
          searchData = JSON.parse(assistantMessage);
        } catch (e) {
          // Not JSON, keep as text
        }
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: assistantMessage,
          searchData: searchData
        }]);
      }
    } catch (error) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Error: Unable to process request. Please try again.'
      }]);
    } finally {
      setIsScanning(false);
    }
  };
  const handleGenerateSearchLinks = () => {
    const searchPrompt = `Generate customized search links for finding business opportunities with these criteria:

FILTERS:
- Price Range: $${params.minPrice.toLocaleString()} - $${params.maxPrice.toLocaleString()}
- SDE Range: $${params.minSDE.toLocaleString()} - $${params.maxSDE.toLocaleString()}
- Min Cash Flow: $${params.minCashFlow.toLocaleString()}
- Min Revenue: $${params.minRevenue.toLocaleString()}
- Max Monthly Rent: $${params.maxRent.toLocaleString()}
- Location: ${params.geography}, ${params.state}
- Business Age: ${params.businessAge}-${params.maxBusinessAge} years
- Employees: ${params.minEmployees}-${params.maxEmployees}
- Min Profit Margin: ${params.profitMargin}%
${params.userAddress ? `- My Location: ${params.userAddress}` : ''}

Provide:
1. Direct search URLs for BizBuySell, BizQuest, and LoopNet with these filters pre-applied
2. Specific things to look for in listings
3. Step-by-step instructions on how to find and evaluate deals
4. Tips for contacting brokers and requesting information

Make the URLs clickable and ready to use immediately.`;
    handleSendMessage(searchPrompt);
  };
  const generatePDF = (deal: any) => {
    const pdf = new jsPDF();
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    let yPos = 20;
    pdf.setFontSize(24);
    pdf.setFont('helvetica', 'bold');
    pdf.text('TRUNG NGUYEN', pageWidth / 2, yPos, {
      align: 'center'
    });
    yPos += 8;
    pdf.setFontSize(12);
    pdf.text('Deal Analysis Report', pageWidth / 2, yPos, {
      align: 'center'
    });
    yPos += 15;
    pdf.setFontSize(10);
    pdf.text('Generated by Deal Sourcing Assistant', 20, yPos);
    yPos += 10;
    pdf.setFontSize(8);
    pdf.setTextColor(128, 128, 128);
    pdf.text('Confidential', pageWidth / 2, pageHeight - 10, {
      align: 'center'
    });
    pdf.save(`deal-analysis-${Date.now()}.pdf`);
  };
  return <>
      <button onClick={() => setIsOpen(true)} className="fixed bottom-20 left-4 opacity-0 hover:opacity-100 transition-opacity duration-300 z-[9998] p-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-full shadow-lg" title="Deal Sourcing (Ctrl+Shift+D)" data-id="element-347">
        <Building2 size={20} data-id="element-348" />
      </button>

      <AnimatePresence data-id="element-349">
        {isOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" data-id="element-350">
            <motion.div initial={{
          scale: 0.9,
          y: 20
        }} animate={{
          scale: 1,
          y: 0
        }} exit={{
          scale: 0.9,
          y: 20
        }} className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-7xl max-h-[95vh] overflow-hidden border-2 border-emerald-500" data-id="element-351">
              <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-4 flex items-center justify-between text-white" data-id="element-352">
                <div className="flex items-center gap-3" data-id="element-353">
                  <Building2 size={24} data-id="element-354" />
                  <div data-id="element-355">
                    <h2 className="text-xl font-bold" data-id="element-356">
                      Deal Sourcing Assistant
                    </h2>
                    <p className="text-emerald-100 text-xs" data-id="element-357">
                      Powered by Trung's AI • Search Strategy & Analysis
                    </p>
                  </div>
                </div>
                <button onClick={() => {
              setIsOpen(false);
              setIsAuthenticated(false);
              setPassword('');
              setMessages([]);
            }} className="text-white/80 hover:text-white hover:bg-white/10 p-2 rounded transition-colors" data-id="element-358">
                  <X size={20} data-id="element-359" />
                </button>
              </div>

              <div className="overflow-y-auto max-h-[calc(95vh-80px)]" data-id="element-360">
                {!isAuthenticated ? <div className="p-8 max-w-md mx-auto" data-id="element-361">
                    <div className="text-center mb-6" data-id="element-362">
                      <div className="w-20 h-20 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-363">
                        <Lock className="w-10 h-10 text-emerald-600 dark:text-emerald-400" data-id="element-364" />
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2" data-id="element-365">
                        Secure Access
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400 text-sm" data-id="element-366">
                        Enter password to access deal sourcing assistant
                      </p>
                    </div>

                    <div className="space-y-4" data-id="element-367">
                      <div className="relative" data-id="element-368">
                        <input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleAuth()} placeholder="Enter password" className="w-full px-4 py-3 pr-12 rounded-xl border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 outline-none" autoFocus data-id="element-369" />
                        <button onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-370">
                          {showPassword ? <EyeOff size={18} data-id="element-371" /> : <Eye size={18} data-id="element-372" />}
                        </button>
                      </div>

                      <button onClick={handleAuth} className="w-full py-3 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-bold hover:from-emerald-700 hover:to-teal-700 shadow-lg" data-id="element-373">
                        Access Platform
                      </button>
                    </div>
                  </div> : <div className="flex h-[calc(95vh-80px)]" data-id="element-374">
                    {/* Parameters Sidebar */}
                    <div className={`${showSettings ? 'w-80' : 'w-0'} transition-all duration-300 overflow-hidden border-r border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-slate-800 overflow-y-auto`} data-id="element-375">
                      <div className="p-6 space-y-6" data-id="element-376">
                        <div className="flex items-center gap-2" data-id="element-377">
                          <Filter className="w-5 h-5 text-emerald-600" data-id="element-378" />
                          <h3 className="font-bold text-lg text-gray-900 dark:text-white" data-id="element-379">
                            Search Criteria
                          </h3>
                        </div>

                        {/* Price Range */}
                        <div data-id="element-380">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3" data-id="element-381">
                            Price: ${params.minPrice.toLocaleString()} - $
                            {params.maxPrice.toLocaleString()}
                          </label>
                          <div className="space-y-2" data-id="element-382">
                            <input type="range" min="100000" max="10000000" step="50000" value={params.minPrice} onChange={e => setParams({
                        ...params,
                        minPrice: Number(e.target.value)
                      })} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-emerald-600" data-id="element-383" />
                            <input type="range" min="100000" max="10000000" step="50000" value={params.maxPrice} onChange={e => setParams({
                        ...params,
                        maxPrice: Number(e.target.value)
                      })} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-emerald-600" data-id="element-384" />
                          </div>
                        </div>

                        {/* SDE Range */}
                        <div data-id="element-385">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3" data-id="element-386">
                            SDE: ${params.minSDE.toLocaleString()} - $
                            {params.maxSDE.toLocaleString()}
                          </label>
                          <div className="space-y-2" data-id="element-387">
                            <input type="range" min="50000" max="2000000" step="25000" value={params.minSDE} onChange={e => setParams({
                        ...params,
                        minSDE: Number(e.target.value)
                      })} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-emerald-600" data-id="element-388" />
                            <input type="range" min="50000" max="2000000" step="25000" value={params.maxSDE} onChange={e => setParams({
                        ...params,
                        maxSDE: Number(e.target.value)
                      })} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-emerald-600" data-id="element-389" />
                          </div>
                        </div>

                        {/* Location */}
                        <div data-id="element-390">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-391">
                            State
                          </label>
                          <input type="text" value={params.state} onChange={e => setParams({
                      ...params,
                      state: e.target.value
                    })} className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-slate-900 text-gray-900 dark:text-white text-sm" data-id="element-392" />
                        </div>

                        <div data-id="element-393">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-394">
                            Geography
                          </label>
                          <input type="text" value={params.geography} onChange={e => setParams({
                      ...params,
                      geography: e.target.value
                    })} className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-slate-900 text-gray-900 dark:text-white text-sm" data-id="element-395" />
                        </div>

                        <div className="pt-4 border-t border-gray-200 dark:border-gray-700" data-id="element-396">
                          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 text-xs text-blue-700 dark:text-blue-300" data-id="element-397">
                            <strong data-id="element-398">How it works:</strong> I'll generate custom
                            search URLs for BizBuySell, BizQuest, and LoopNet
                            with your filters. You browse, find deals, then
                            paste them here for analysis.
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Chat Interface */}
                    <div className="flex-1 flex flex-col" data-id="element-399">
                      <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between bg-white dark:bg-slate-900" data-id="element-400">
                        <div className="flex gap-2" data-id="element-401">
                          <button onClick={() => setShowSettings(!showSettings)} className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 ${showSettings ? 'bg-emerald-600 text-white' : 'bg-gray-100 dark:bg-slate-800 text-gray-600'}`} data-id="element-402">
                            <Settings size={18} data-id="element-403" />
                            Criteria
                          </button>
                          <button onClick={handleGenerateSearchLinks} disabled={isScanning} className="px-4 py-2 rounded-lg bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-medium flex items-center gap-2 disabled:opacity-50" data-id="element-404">
                            {isScanning ? <>
                                <Loader2 size={18} className="animate-spin" data-id="element-405" />
                                Generating...
                              </> : <>
                                <Globe size={18} data-id="element-406" />
                                Generate Search Links
                              </>}
                          </button>
                        </div>
                      </div>

                      {/* Messages */}
                      <div className="flex-1 overflow-y-auto p-6 bg-gray-50 dark:bg-slate-950" data-id="element-407">
                        <div className="max-w-4xl mx-auto space-y-4" data-id="element-408">
                          {messages.map((msg: any, idx: number) => <div key={idx} className={`flex items-start gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`} data-id="element-409">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${msg.role === 'user' ? 'bg-emerald-100 text-emerald-600' : 'bg-blue-100 text-blue-600'}`} data-id="element-410">
                                <Bot size={16} data-id="element-411" />
                              </div>
                              <div className={`max-w-[85%] p-4 rounded-2xl ${msg.role === 'user' ? 'bg-emerald-600 text-white rounded-tr-none' : 'bg-white dark:bg-slate-800 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700 rounded-tl-none'}`} data-id="element-412">
                                {msg.searchData?.search_strategy ? <div className="space-y-4" data-id="element-413">
                                    <h3 className="font-bold text-lg mb-3" data-id="element-414">
                                      Your Custom Search Strategy
                                    </h3>

                                    {msg.searchData.search_strategy.platforms.map((platform: any, pIdx: number) => <div key={pIdx} className="bg-gray-50 dark:bg-slate-900 rounded-lg p-4 border border-gray-200 dark:border-gray-700" data-id="element-415">
                                          <div className="flex items-center justify-between mb-3" data-id="element-416">
                                            <h4 className="font-bold text-emerald-600 dark:text-emerald-400" data-id="element-417">
                                              {platform.name}
                                            </h4>
                                            <a href={platform.search_url} target="_blank" rel="noopener noreferrer" className="px-3 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium flex items-center gap-1" data-id="element-418">
                                              <Search size={14} data-id="element-419" />
                                              Search Now
                                              <ExternalLink size={12} data-id="element-420" />
                                            </a>
                                          </div>

                                          <div className="space-y-2 text-sm" data-id="element-421">
                                            <div data-id="element-422">
                                              <strong className="text-gray-700 dark:text-gray-300" data-id="element-423">
                                                Filters Applied:
                                              </strong>
                                              <ul className="mt-1 space-y-1" data-id="element-424">
                                                {platform.filters_to_apply.map((filter: string, fIdx: number) => <li key={fIdx} className="text-gray-600 dark:text-gray-400 flex items-center gap-2" data-id="element-425">
                                                      <CheckCircle size={12} className="text-emerald-500" data-id="element-426" />
                                                      {filter}
                                                    </li>)}
                                              </ul>
                                            </div>

                                            <div data-id="element-427">
                                              <strong className="text-gray-700 dark:text-gray-300" data-id="element-428">
                                                What to Look For:
                                              </strong>
                                              <ul className="mt-1 space-y-1" data-id="element-429">
                                                {platform.what_to_look_for.map((tip: string, tIdx: number) => <li key={tIdx} className="text-gray-600 dark:text-gray-400" data-id="element-430">
                                                      • {tip}
                                                    </li>)}
                                              </ul>
                                            </div>
                                          </div>
                                        </div>)}

                                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800" data-id="element-431">
                                      <h4 className="font-bold text-blue-900 dark:text-blue-100 mb-2" data-id="element-432">
                                        Next Steps:
                                      </h4>
                                      <ol className="space-y-2 text-sm text-blue-800 dark:text-blue-200" data-id="element-433">
                                        {msg.searchData.search_strategy.next_steps.map((step: string, sIdx: number) => <li key={sIdx} data-id="element-434">{step}</li>)}
                                      </ol>
                                    </div>

                                    {msg.searchData.search_strategy.pro_tips && <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4 border border-yellow-200 dark:border-yellow-800" data-id="element-435">
                                        <h4 className="font-bold text-yellow-900 dark:text-yellow-100 mb-2" data-id="element-436">
                                          Pro Tips:
                                        </h4>
                                        <ul className="space-y-1 text-sm text-yellow-800 dark:text-yellow-200" data-id="element-437">
                                          {msg.searchData.search_strategy.pro_tips.map((tip: string, tIdx: number) => <li key={tIdx} data-id="element-438">💡 {tip}</li>)}
                                        </ul>
                                      </div>}
                                  </div> : <div className="prose prose-sm dark:prose-invert max-w-none" data-id="element-439">
                                    <p className="whitespace-pre-wrap" data-id="element-440">
                                      {msg.content}
                                    </p>
                                  </div>}
                              </div>
                            </div>)}

                          {isScanning && <div className="flex items-start gap-3" data-id="element-441">
                              <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center" data-id="element-442">
                                <Bot size={16} data-id="element-443" />
                              </div>
                              <div className="bg-white dark:bg-slate-800 p-4 rounded-2xl border border-gray-200 dark:border-gray-700" data-id="element-444">
                                <div className="flex gap-1" data-id="element-445">
                                  <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{
                            animationDelay: '0ms'
                          }} data-id="element-446"></span>
                                  <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{
                            animationDelay: '150ms'
                          }} data-id="element-447"></span>
                                  <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{
                            animationDelay: '300ms'
                          }} data-id="element-448"></span>
                                </div>
                              </div>
                            </div>}
                          <div ref={messagesEndRef} data-id="element-449" />
                        </div>
                      </div>

                      {/* Input */}
                      <div className="p-3 bg-white dark:bg-slate-900 border-t border-gray-200 dark:border-gray-700" data-id="element-450">
                        <div className="flex items-center gap-2" data-id="element-451">
                          <input type="text" value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSendMessage()} placeholder="Paste deal details for analysis, or ask questions..." className="flex-1 px-4 py-2 rounded-full bg-gray-100 dark:bg-slate-800 text-gray-900 dark:text-white outline-none text-sm" disabled={isScanning} data-id="element-452" />
                          <button onClick={() => handleSendMessage()} disabled={!input.trim() || isScanning} className="p-2 text-emerald-600 disabled:text-gray-400 hover:scale-110 transition-transform" data-id="element-453">
                            <Send size={18} data-id="element-454" />
                          </button>
                        </div>
                        <p className="text-xs text-gray-500 text-center mt-2" data-id="element-455">
                          I'll generate search links and analyze deals you find
                        </p>
                      </div>
                    </div>
                  </div>}
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
};